import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import creditService from '../../lib/creditService'

const CreditDisplay = ({ compact = false, showHistory = false, dashboardLayout = false, onRefreshCredits }) => {
  const { user } = useAuth()
  const [credits, setCredits] = useState(null)
  const [loading, setLoading] = useState(true)
  const [history, setHistory] = useState([])
  const [showHistoryModal, setShowHistoryModal] = useState(false)

  const loadCredits = useCallback(async () => {
    try {
      setLoading(true)
      const creditData = await creditService.getUserCredits(user.id)
      setCredits(creditData)
    } catch (error) {
      console.error('Error loading credits:', error)
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    if (user) {
      loadCredits()
    }
  }, [user, loadCredits])

  // Expose refresh function to parent via callback (only once)
  useEffect(() => {
    if (onRefreshCredits) {
      onRefreshCredits(loadCredits)
    }
  }, [onRefreshCredits]) // Remove loadCredits from dependencies to prevent infinite loop

  const loadHistory = async () => {
    try {
      const historyData = await creditService.getCreditHistory(user.id, 20)
      setHistory(historyData)
      setShowHistoryModal(true)
    } catch (error) {
      console.error('Error loading credit history:', error)
    }
  }

  const getCreditColor = (current, max) => {
    const percentage = (current / max) * 100
    if (percentage > 50) return 'text-green-600'
    if (percentage > 20) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'pro':
        return 'bg-purple-100 text-purple-800'
      case 'beta':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div className={`${compact ? 'p-2' : 'p-4'} animate-pulse`}>
        <div className="h-4 bg-gray-200 rounded mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      </div>
    )
  }

  if (!credits) {
    return null
  }

  if (compact) {
    return (
      <div className="flex items-center space-x-4 text-sm">
        <div className="flex items-center space-x-1">
          <span className="text-gray-500">🚀</span>
          <span className={getCreditColor(credits.projectCredits, credits.planQuotas.project)}>
            {credits.projectCredits}
          </span>
          <span className="text-gray-400">/{credits.planQuotas.project}</span>
        </div>
        <div className="flex items-center space-x-1">
          <span className="text-gray-500">🎨</span>
          <span className={getCreditColor(credits.imageCredits, credits.planQuotas.image)}>
            {credits.imageCredits}
          </span>
          <span className="text-gray-400">/{credits.planQuotas.image}</span>
        </div>
      </div>
    )
  }

  if (dashboardLayout) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Project Generation */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <span className="text-xl">🚀</span>
              <div>
                <p className="font-medium text-gray-900">Project Generation</p>
                <p className="text-sm text-gray-500">AI project creation</p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className={`text-2xl font-bold ${getCreditColor(credits.projectCredits, credits.planQuotas.project)}`}>
              {credits.projectCredits}
            </div>
            <div className="text-sm text-gray-500">
              of {credits.planQuotas.project}
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                credits.projectCredits > credits.planQuotas.project * 0.5 ? 'bg-green-500' :
                credits.projectCredits > credits.planQuotas.project * 0.2 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${Math.min((credits.projectCredits / credits.planQuotas.project) * 100, 100)}%` }}
            ></div>
          </div>
        </div>

        {/* Image Generation */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <span className="text-xl">🎨</span>
              <div>
                <p className="font-medium text-gray-900">Image Generation</p>
                <p className="text-sm text-gray-500">AI image creation</p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className={`text-2xl font-bold ${getCreditColor(credits.imageCredits, credits.planQuotas.image)}`}>
              {credits.imageCredits}
            </div>
            <div className="text-sm text-gray-500">
              of {credits.planQuotas.image}
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                credits.imageCredits > credits.planQuotas.image * 0.5 ? 'bg-green-500' :
                credits.imageCredits > credits.planQuotas.image * 0.2 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${Math.min((credits.imageCredits / credits.planQuotas.image) * 100, 100)}%` }}
            ></div>
          </div>
        </div>

        {/* Credit History & Upgrade */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <span className="text-xl">📊</span>
              <div>
                <p className="font-medium text-gray-900">Plan & History</p>
                <p className="text-sm text-gray-500">
                  {credits.plan === 'beta' ? 'Beta' : credits.plan.toUpperCase()} plan
                </p>
              </div>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPlanBadgeColor(credits.plan)}`}>
              {credits.plan === 'beta' ? 'Beta' : credits.plan.toUpperCase()}
            </span>
          </div>
          <div className="space-y-3">
            <button
              onClick={loadHistory}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
            >
              View Credit History
            </button>
            {credits.plan === 'free' && (
              <a
                href="/plan"
                className="block w-full text-center px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Upgrade to Pro
              </a>
            )}
          </div>
          <div className="mt-3 text-xs text-gray-500">
            Credits reset monthly
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">AI Credits</h3>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPlanBadgeColor(credits.plan)}`}>
          {credits.plan === 'beta' ? 'Beta' : credits.plan.toUpperCase()}
        </span>
      </div>

      <div className="space-y-4">
        {/* Project Credits */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-xl">🚀</span>
            <div>
              <p className="font-medium text-gray-900">Project Generation</p>
              <p className="text-sm text-gray-500">Create full AI projects</p>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-lg font-bold ${getCreditColor(credits.projectCredits, credits.planQuotas.project)}`}>
              {credits.projectCredits}
            </div>
            <div className="text-sm text-gray-500">
              of {credits.planQuotas.project}
            </div>
            <div className="w-16 bg-gray-200 rounded-full h-2 mt-1">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  credits.projectCredits > credits.planQuotas.project * 0.5 ? 'bg-green-500' :
                  credits.projectCredits > credits.planQuotas.project * 0.2 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${Math.min((credits.projectCredits / credits.planQuotas.project) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Image Credits */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-xl">🎨</span>
            <div>
              <p className="font-medium text-gray-900">Image Generation</p>
              <p className="text-sm text-gray-500">Generate AI images in editor</p>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-lg font-bold ${getCreditColor(credits.imageCredits, credits.planQuotas.image)}`}>
              {credits.imageCredits}
            </div>
            <div className="text-sm text-gray-500">
              of {credits.planQuotas.image}
            </div>
            <div className="w-16 bg-gray-200 rounded-full h-2 mt-1">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  credits.imageCredits > credits.planQuotas.image * 0.5 ? 'bg-green-500' :
                  credits.imageCredits > credits.planQuotas.image * 0.2 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${Math.min((credits.imageCredits / credits.planQuotas.image) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {showHistory && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <button
            onClick={loadHistory}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            View Credit History
          </button>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500">
        Credits reset monthly. {credits.plan === 'free' && (
          <a href="/plan" className="text-blue-600 hover:underline">Upgrade for more credits</a>
        )}
      </div>

      {/* Credit History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-96 overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Credit History</h3>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto max-h-80">
              {history.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No credit history yet</p>
              ) : (
                <div className="space-y-2">
                  {history.map(transaction => (
                    <div key={transaction.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                      <div>
                        <p className="font-medium text-sm">
                          {creditService.formatCreditType(transaction.credit_type)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {creditService.formatCreditSource(transaction.source)}
                          {transaction.description && ` - ${transaction.description}`}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(transaction.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className={`font-bold text-sm ${
                        transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CreditDisplay
